import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { loadCalendarFromLocalStorage } from '@/lib/calendarParser'
import Card from '@/components/common/Card'
import Select from '@/components/common/Select'
import Input from '@/components/common/Input'
import Button from '@/components/common/Button'
import { HOCKEY_AGE_GROUPS, getAvailableTiers, DISTANCE_PRESETS } from '@/lib/hockeyData'
import { toast } from 'sonner'

// =============================================================================
// FOCUS MODE: Squirt B1 Only - matches useScrimmageSearch.js
// =============================================================================
const FOCUS_LEVEL = 'boys_squirt_10u'
const FOCUS_LEVEL_DETAIL = 'B1'
const FOCUS_MODE_ENABLED = true  // Set to false to unlock all levels

export default function ScrimmageFilters({ filters, onFilterChange, onClearFilters, onUploadCalendar, teamId }) {
  const [locationInput, setLocationInput] = useState('')
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [hasCalendar, setHasCalendar] = useState(false)
  const [calendarName, setCalendarName] = useState('')
  const [lastSearchedLocation, setLastSearchedLocation] = useState('')
  
  // Check for calendar (localStorage or logged-in user)
  useEffect(() => {
    checkForCalendar()
  }, [])
  
  const checkForCalendar = async () => {
    // Check localStorage first (public users)
    const localCal = loadCalendarFromLocalStorage()
    if (localCal) {
      setHasCalendar(true)
      setCalendarName(localCal.calendarName || 'Your Calendar')
      return
    }
    
    // Check if logged-in user has calendar
    const { data: { session } } = await supabase.auth.getSession()
    if (session) {
      const { data: team } = await supabase
        .from('teams')
        .select('id, calendar_verified')
        .eq('user_id', session.user.id)
        .single()
      
      if (team?.calendar_verified) {
        setHasCalendar(true)
        setCalendarName(team.name || 'Team Calendar')
      }
    }
  }

  const handleLocationSearch = async () => {
    if (isGeocoding) return
    if (!locationInput.trim()) {
      onFilterChange('searchLat', null)
      onFilterChange('searchLng', null)
      onFilterChange('radiusMiles', null)
      setLastSearchedLocation('')
      return
    }

    setIsGeocoding(true)
    
    try {
      // Use OpenStreetMap Nominatim for geocoding (free, no API key required)
      // countrycodes=us prioritizes US locations for zip codes
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationInput)}&countrycodes=us&limit=1`
      )
      const data = await response.json()

      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0]
        onFilterChange('searchLat', parseFloat(lat))
        onFilterChange('searchLng', parseFloat(lon))
        onFilterChange('radiusMiles', 90) // Default to 90 miles
        setLastSearchedLocation(locationInput.trim())
        toast.success(`Location set to ${display_name.split(',')[0] || display_name}`)
      } else {
        toast.error('Location not found. Please try a different search term.')
      }
    } catch (err) {
      console.error('Geocoding error:', err)
      toast.error('Failed to search location. Please try again.')
    } finally {
      setIsGeocoding(false)
    }
  }

  const handleClearLocation = () => {
    setLocationInput('')
    onFilterChange('searchLat', null)
    onFilterChange('searchLng', null)
    onFilterChange('radiusMiles', null)
    setLastSearchedLocation('')
  }

  const handleLocationBlur = () => {
    const trimmed = locationInput.trim()
    if (!trimmed) {
      return
    }
    if (trimmed !== lastSearchedLocation) {
      handleLocationSearch()
    }
  }

  const availableTiers = filters.level ? [{ value: '', label: 'All tiers' }, ...getAvailableTiers(filters.level)] : [{ value: '', label: 'All tiers' }]

  const hasActiveFilters = 
    filters.level || 
    filters.levelDetail || 
    filters.dateStart || 
    filters.dateEnd || 
    filters.postingType || 
    filters.refereeType || 
    filters.homeAndHome ||
    filters.searchLat ||
    filters.radiusMiles

  const activeFilterCount = [
    filters.level,
    filters.levelDetail,
    filters.dateStart,
    filters.dateEnd,
    filters.postingType,
    filters.refereeType,
    filters.homeAndHome,
    filters.searchLat
  ].filter(Boolean).length

  return (
    <Card>
      {/* Mobile: Collapsible header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="lg:hidden w-full flex items-center justify-between mb-4"
      >
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-lg font-bold">Filters</h2>
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-brand-green rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Desktop: Always visible header */}
      <h2 className="hidden lg:block font-heading text-lg font-bold mb-4">Filters</h2>

      <div className={`space-y-4 ${!isExpanded ? 'hidden lg:block' : ''}`}>
        {/* Location Search */}
        <div>
          <label className="label mb-1">Search Near</label>
          <div className="space-y-2">
            <Input
              placeholder="City, State or Zip"
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLocationSearch()}
              onBlur={handleLocationBlur}
            />
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleLocationSearch}
                disabled={isGeocoding || !locationInput.trim()}
                className="flex-1"
              >
                {isGeocoding ? 'Searching...' : 'Search'}
              </Button>
              {(filters.searchLat || locationInput) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleClearLocation}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {filters.searchLat && (
            <Select
              label="Radius"
              value={filters.radiusMiles || ''}
              onChange={(e) => onFilterChange('radiusMiles', e.target.value ? parseInt(e.target.value) : null)}
              options={DISTANCE_PRESETS}
              className="mt-2"
            />
          )}
        </div>

        {/* Age Group */}
        <div>
          <Select
            label="Age Group"
            value={FOCUS_MODE_ENABLED ? FOCUS_LEVEL : (filters.level || '')}
            onChange={(e) => {
              if (FOCUS_MODE_ENABLED) return // Locked in focus mode
              onFilterChange('level', e.target.value || null)
              onFilterChange('levelDetail', null) // Reset tier
            }}
            options={[
              { value: '', label: 'All age groups' },
              ...HOCKEY_AGE_GROUPS
            ]}
            disabled={FOCUS_MODE_ENABLED}
          />
          {FOCUS_MODE_ENABLED && (
            <p className="text-xs text-amber-600 mt-1">🎯 Focus mode: Squirt B1 only</p>
          )}
        </div>

        {/* Team Tier */}
        <Select
          label="Team Tier"
          value={FOCUS_MODE_ENABLED ? FOCUS_LEVEL_DETAIL : (filters.levelDetail || '')}
          onChange={(e) => {
            if (FOCUS_MODE_ENABLED) return // Locked in focus mode
            onFilterChange('levelDetail', e.target.value || null)
          }}
          options={availableTiers}
          disabled={FOCUS_MODE_ENABLED || !filters.level || getAvailableTiers(filters.level).length === 0}
        />

        {/* Date Range */}
        <div>
          <label className="label mb-1">Date Range</label>
          <div className="space-y-2">
            <Input
              type="date"
              value={filters.dateStart || ''}
              onChange={(e) => onFilterChange('dateStart', e.target.value || null)}
              placeholder="From"
            />
            <Input
              type="date"
              value={filters.dateEnd || ''}
              onChange={(e) => onFilterChange('dateEnd', e.target.value || null)}
              placeholder="To"
            />
          </div>
        </div>

        {/* Posting Type */}
        <Select
          label="Type"
          value={filters.postingType || ''}
          onChange={(e) => {
            const newType = e.target.value || null
            onFilterChange('postingType', newType)
            // Clear referee type and home-and-home when switching to "Seeking Away Game"
            // since these filters don't apply to "wanted" postings
            if (newType === 'wanted') {
              onFilterChange('refereeType', null)
              onFilterChange('homeAndHome', null)
            }
          }}
          options={[
            { value: '', label: 'All Types' },
            { value: 'available', label: 'Home Ice Available' },
            { value: 'wanted', label: 'Seeking Away Game' }
          ]}
        />

        {/* Referee Type - Only applies to "Available" postings */}
        {filters.postingType !== 'wanted' && (
          <Select
            label="Referee Type"
            value={filters.refereeType || ''}
            onChange={(e) => onFilterChange('refereeType', e.target.value || null)}
            options={[
              { value: '', label: 'Any' },
              { value: 'none', label: 'No referees' },
              { value: 'coach_led', label: 'Coach-led' },
              { value: 'paid_refs', label: 'Paid referees' }
            ]}
          />
        )}

        {/* Home and Home - Only applies to "Available" postings */}
        {filters.postingType !== 'wanted' && (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.homeAndHome || false}
              onChange={(e) => onFilterChange('homeAndHome', e.target.checked || null)}
              className="mr-2 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
            />
            <span className="text-sm text-brand-charcoal">Only show home-and-home swaps</span>
          </label>
        )}

        {teamId && (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.includeOwnTeam || false}
              onChange={(e) => onFilterChange('includeOwnTeam', e.target.checked)}
              className="mr-2 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
            />
            <span className="text-sm text-brand-charcoal">Include my team’s postings</span>
          </label>
        )}

        {/* Calendar Match Filter */}
        <div className="pt-4 border-t border-gray-200">
          <h3 className="font-semibold text-brand-charcoal mb-2">Match My Calendar</h3>
          
          {!hasCalendar ? (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">
                Upload your team calendar to see only scrimmages that work with your schedule
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={onUploadCalendar}
                className="w-full"
              >
                📅 Upload Calendar
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.calendarEnabled || false}
                  onChange={(e) => {
                    onFilterChange('calendarEnabled', e.target.checked)
                    if (!e.target.checked) {
                      onFilterChange('conflictMode', null)
                    }
                  }}
                  className="mr-2 h-4 w-4 text-brand-green border-gray-300 rounded focus:ring-brand-green"
                />
                <span className="text-sm text-brand-charcoal">Filter by my schedule</span>
              </label>
              
              {filters.calendarEnabled && (
                <>
                  <div className="pl-6 space-y-2">
                    <p className="text-xs text-gray-500 mb-2">
                      Filtering by: <strong>{calendarName}</strong>
                    </p>
                    
                    <div className="space-y-1">
                      <label className="flex items-center text-sm">
                        <input
                          type="radio"
                          name="conflictMode"
                          value="strict"
                          checked={filters.conflictMode === 'strict'}
                          onChange={(e) => onFilterChange('conflictMode', e.target.value)}
                          className="mr-2 h-4 w-4 text-brand-green border-gray-300 focus:ring-brand-green"
                        />
                        <span>🟢 Only free days</span>
                      </label>
                      
                      <label className="flex items-center text-sm">
                        <input
                          type="radio"
                          name="conflictMode"
                          value="flexible"
                          checked={filters.conflictMode === 'flexible'}
                          onChange={(e) => onFilterChange('conflictMode', e.target.value)}
                          className="mr-2 h-4 w-4 text-brand-green border-gray-300 focus:ring-brand-green"
                        />
                        <span>🟡 Non-overlapping times</span>
                      </label>
                      
                      <label className="flex items-center text-sm">
                        <input
                          type="radio"
                          name="conflictMode"
                          value="any"
                          checked={filters.conflictMode === 'any' || !filters.conflictMode}
                          onChange={(e) => onFilterChange('conflictMode', e.target.value)}
                          className="mr-2 h-4 w-4 text-brand-green border-gray-300 focus:ring-brand-green"
                        />
                        <span>🔴 Show all (with warnings)</span>
                      </label>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onFilterChange('calendarEnabled', false)
                      onFilterChange('conflictMode', null)
                    }}
                    className="w-full"
                  >
                    Clear Calendar Filter
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Mobile: Apply & Clear Buttons */}
        <div className="lg:hidden flex gap-2 pt-2">
          <Button
            variant="primary"
            onClick={() => setIsExpanded(false)}
            className="flex-1"
          >
            Apply Filters
          </Button>
          {hasActiveFilters && (
            <Button
              variant="secondary"
              onClick={() => {
                onClearFilters()
                setIsExpanded(false)
              }}
              className="flex-1"
            >
              Clear All
            </Button>
          )}
        </div>

        {/* Desktop: Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="secondary"
            onClick={onClearFilters}
            className="hidden lg:block w-full"
          >
            Clear All Filters
          </Button>
        )}
      </div>
    </Card>
  )
}

